import { useState, useEffect } from 'react';
import { functions, auth } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth';
import { dataManager } from '../utils/dataManager';

interface AuthState {
  isAuthenticated: boolean;
  isAdmin: boolean;
  loading: boolean;
}

// Local storage keys for authentication state
const AUTH_STATE_KEY = 'kfppc_auth_state';

// Module-level flag to ensure reconnectFirestore is only called once
// across all useAuth instances (prevents mount/unmount loop)
let hasReconnectedFirestore = false;

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isAdmin: false,
    loading: true,
  });

  useEffect(() => {
    // Listen for Firebase Auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          // Get token result to check if claims are already present
          let tokenResult = await user.getIdTokenResult();
          let role = tokenResult.claims.role as string | undefined;

          // Fallback: only force-refresh token if claims are not yet present in cached token
          if (!role) {
            console.log('KFPPC [Auth]: Role claim not found in cached token, fetching fresh token...');
            await user.getIdToken(true);
            tokenResult = await user.getIdTokenResult();
            role = tokenResult.claims.role as string | undefined;
          }

          setAuthState({
            isAuthenticated: true,
            isAdmin: role === 'admin',
            loading: false,
          });

          // Re-establish Firestore listener now that auth is confirmed.
          // Only do this ONCE to fix race condition where DataManager's initial
          // listener may have connected before Firebase Auth restored the session token.
          // Without this guard, multiple useAuth() instances (App.tsx + AdminView.tsx)
          // would each call reconnectFirestore() on mount, resetting isInitialSyncComplete
          // and causing an infinite flash loop.
          if (!hasReconnectedFirestore) {
            hasReconnectedFirestore = true;
            dataManager.reconnectFirestore();
          }

          // Also store in localStorage for quick restore
          localStorage.setItem(AUTH_STATE_KEY, JSON.stringify({
            role: role || 'user',
            authType: role === 'admin' ? 'admin' : 'app',
            timestamp: Date.now()
          }));
        } else {
          // No Firebase user, check localStorage for quick restore while re-authenticating
          const storedAuthState = localStorage.getItem(AUTH_STATE_KEY);
          if (storedAuthState) {
            // Clear stale localStorage since there's no Firebase session
            localStorage.removeItem(AUTH_STATE_KEY);
          }
          // Reset reconnect flag on sign-out so next sign-in reconnects properly
          hasReconnectedFirestore = false;
          setAuthState({
            isAuthenticated: false,
            isAdmin: false,
            loading: false,
          });
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        // On error, set to unauthenticated state so app doesn't hang
        setAuthState({
          isAuthenticated: false,
          isAdmin: false,
          loading: false,
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const loginWithAppPassword = async (password: string): Promise<boolean> => {
    try {
      // Call Cloud Function to verify password and get custom token
      const verifyAppPassword = httpsCallable<{ password: string }, { success: boolean; role: string; authType: string; customToken: string }>(
        functions,
        'verifyAppPassword'
      );
      const result = await verifyAppPassword({ password });

      if (result.data.success && result.data.customToken) {
        // Sign in with the custom token to establish Firebase Auth session
        const userCredential = await signInWithCustomToken(auth, result.data.customToken);
        // Get token result to verify claims are present
        const tokenResult = await userCredential.user.getIdTokenResult();
        if (!tokenResult.claims.role) {
          // Fallback force-refresh if claims are not yet available
          await userCredential.user.getIdToken(true);
        }
        // Auth state will be updated by onAuthStateChanged listener
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('App login failed:', error);
      return false;
    }
  };

  const loginWithAdminPassword = async (password: string): Promise<boolean> => {
    try {
      const verifyAdminPassword = httpsCallable<{ password: string }, { success: boolean; role: string; authType: string; customToken: string }>(
        functions,
        'verifyAdminPassword'
      );
      const result = await verifyAdminPassword({ password });

      if (result.data.success && result.data.customToken) {
        const userCredential = await signInWithCustomToken(auth, result.data.customToken);
        // Get token result to verify claims are present
        const tokenResult = await userCredential.user.getIdTokenResult();
        if (!tokenResult.claims.role) {
          // Fallback force-refresh if claims are not yet available
          await userCredential.user.getIdToken(true);
        }
        // Auth state will be updated by onAuthStateChanged listener
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('[Auth] Admin login error:', error?.code, error?.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      // Sign out from Firebase Auth
      await signOut(auth);
      // Clear local storage
      localStorage.removeItem(AUTH_STATE_KEY);
      // Auth state will be updated by onAuthStateChanged listener
    } catch (error) {
      console.error('Logout failed:', error);
      // Force clear state even if signOut fails
      localStorage.removeItem(AUTH_STATE_KEY);
      setAuthState({
        isAuthenticated: false,
        isAdmin: false,
        loading: false,
      });
    }
  };

  return {
    ...authState,
    loginWithAppPassword,
    loginWithAdminPassword,
    logout
  };
}
